<template>
    <div class="cart">
      <div class="configurator-header">
        <div class="logo-container">
          <img src="https://sander-gruppe.eu/wp-content/uploads/2019/07/ms-logo.png" alt="Ihr Logo" class="logo" @click="goToDashboard">
        </div>
        <div class="flex-down">
          <div class="component-container-header">
            Warenkorb
          </div>
          <div v-if="selectedProject" class="component-container-header">
            Projekt: {{ selectedProject != null ? selectedProject.name : "" }}
          </div>
        </div>
      </div>
      <div
        v-if="showInternalProjectInfo"
        class="internal-project-info"
        :class="{ 'internal-project-info-single': !showInternalSperrgrund }"
      >
        <div v-if="showInternalSperrgrund" class="internal-project-panel internal-block-reason">
          <label for="internalSperrgrund">Sperrgrund</label>
          <textarea
            id="internalSperrgrund"
            :value="internalSperrgrundText"
            rows="4"
            readonly
          ></textarea>
        </div>
        <div v-if="showInternalProjectNote" class="internal-project-panel internal-project-note">
          <label for="internalProjectNote">Interne Notiz</label>
          <textarea
            id="internalProjectNote"
            v-model="selectedProject.internal_note"
            rows="4"
            placeholder="Interne Notiz zu diesem Projekt"
            @blur="updateInternalProjectNote"
          ></textarea>
        </div>
      </div>
      <div v-if="isProjectReadOnly" class="project-readonly-banner" role="status">
        <i class="fa fa-lock"></i>
        <div>
          <strong>Projekt gesperrt</strong>
          <span>{{ projectReadOnlyMessage }}</span>
        </div>
      </div>
      <div v-if="selectedProject" class="project-notes">
        <textarea 
          id="projectNotes"
          v-model="selectedProject.notizen"
          @blur="updateProjectNotes"
          :readonly="isProjectReadOnly"
          :class="{ 'readonly-textarea': isProjectReadOnly }"
          placeholder="Geben Sie hier Ihre Notizen ein"
          rows="5"
          cols="50"
        ></textarea>
      </div>
      <div
        v-if="orderSaveMessage"
        class="cart-order-status"
        role="status"
        aria-live="polite"
      >
        {{ orderSaveMessage }}
      </div>
      <template
        v-for="(item, index) in cartItems"
        :key="item.id ?? item.pos ?? item.positionBez"
      >
        <CartItem
          ref="cartItemsRefs"
          :pos="item.pos"
          :id="item.id"
          :articleId="item.articleId"
          :articleSearchText="item.articleSearchText"
          :priorityFolders="item.priorityFolders"
          :positionBez="item.positionBez"
          :items="item.items"
          :price="item.price"
          :originalPrice="item.originalPrice"
          :manualListPrice="item.manualListPrice"
          :productImage="item.productImage"
          :amount="item.amount"
          :notes="item.notes"
          :discount="item.discount"
          :manualTotalPrice="item.manualTotalPrice"
          :teuerungszuschlag="item.teuerungszuschlag"
          :isFreeText="item.isFreeText"
          :isAlternativePosition="item.isAlternativePosition"
          :isInternalAdmin="isInternalAdmin"
          :isReadOnly="isProjectReadOnly"
          :canMoveUp="index > 0"
          :canMoveDown="index < cartItems.length - 1"
          :isOrderSaving="isSavingCartOrder"
          :isDragging="draggedCartItemId === item.id"
          :dropPlacement="dragOverCartItemId === item.id ? dragOverPlacement : null"
          @image-loaded="updateProductImage"
          @remove-item="confirmRemoveItemFromCart"
          @copy-item="confirmCopyItemToCart"
          @open-item="openItemInMainComponent"
          @list-price-changed="handleListPriceChanged"
          @quantity-changed="handleQuantityChanged"
          @discount-changed="handleDiscountChanged"
          @manual-total-changed="handleManualTotalChanged"
          @free-text-details-changed="handleFreeTextDetailsChanged"
          @alternative-position-changed="handleAlternativePositionChanged"
          @insert-free-text-after="openFreeTextDialog"
          @move-item="moveCartItemByDirection"
          @drag-start-item="startCartItemDrag"
          @drag-over-item="updateCartItemDropTarget"
          @drop-on-item="dropCartItem"
          @drag-end-item="endCartItemDrag"
        />
        <div
          v-if="shouldShowInlineFreeTextEditorAfter(item)"
          class="cart-inline-editor free-text-inline-editor"
        >
          <div class="cart-inline-editor-title">Freitextposition</div>
          <div class="free-text-inline-grid">
            <label>
              <span>Raum</span>
              <input
                type="text"
                v-model="freeTextPositionName"
                maxlength="7"
                placeholder="Raum"
              >
            </label>
            <label class="free-text-inline-content">
              <span>Freitext</span>
              <textarea
                v-model="freeTextContent"
                :class="{ 'free-text-required-input': isFreeTextDraftEmpty }"
                rows="3"
                maxlength="500"
                placeholder="Freitext eingeben"
              ></textarea>
            </label>
            <label>
              <span>Pos. GP/€</span>
              <div class="inline-input-with-suffix">
                <input
                  type="number"
                  class="no-spinner"
                  v-model.number="freeTextTotal"
                  step="0.01"
                  placeholder="+/- 0,00"
                >
                <span>€</span>
              </div>
            </label>
          </div>
          <div class="cart-inline-editor-actions">
            <button type="button" @click="createFreeTextPosition">Anlegen</button>
            <button type="button" @click="closeInlineFreeTextEditor">Abbrechen</button>
          </div>
        </div>
      </template>
    </div>
    <div class="sum">
      <div class="rows">
        <div class="row">
          <div class="space"></div>
          <div class="left">Nettowert</div>
          <div class="right">{{ formatSummaryAmount(netto) }}</div>
        </div>
        <div
          v-for="adjustment in summaryAdjustments"
          :key="adjustment.id ?? adjustment.text"
          class="row summary-adjustment-row"
        >
          <div class="space"></div>
          <div
            class="left"
            :class="{ 'summary-adjustment-editing': isInternalAdmin && !isProjectReadOnly && !adjustment.isPowderFee }"
          >
            <template v-if="isInternalAdmin && !isProjectReadOnly && !adjustment.isPowderFee">
              <input
                type="text"
                class="summary-adjustment-text-input"
                :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentTextEmpty(adjustment.text) }"
                v-model="adjustment.text"
                maxlength="80"
                @change="persistSummaryAdjustment(adjustment)"
              >
              <div class="summary-adjustment-signed-fields">
                <div
                  class="summary-adjustment-value-wrap"
                  :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentInvalid(adjustment) || isSummaryAdjustmentDiscountOverLimit(adjustment) }"
                >
                  <input
                    type="number"
                    class="summary-adjustment-value-input no-spinner"
                    :value="adjustment.percentValue"
                    step="0.01"
                    placeholder="0"
                    @input="handleSummaryAdjustmentPercentInput(adjustment, $event)"
                    @change="persistSummaryAdjustment(adjustment)"
                  >
                  <span>%</span>
                </div>
                <div
                  class="summary-adjustment-value-wrap"
                  :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentInvalid(adjustment) || isSummaryAdjustmentDiscountOverLimit(adjustment) }"
                >
                  <input
                    type="number"
                    class="summary-adjustment-value-input no-spinner"
                    :value="adjustment.amountValue"
                    step="0.01"
                    placeholder="0"
                    @input="handleSummaryAdjustmentAmountInput(adjustment, $event)"
                    @change="persistSummaryAdjustment(adjustment)"
                  >
                  <span>€</span>
                </div>
                <button
                  type="button"
                  class="summary-adjustment-delete"
                  title="Sonderrabatt löschen"
                  aria-label="Sonderrabatt löschen"
                  @click="removeSummaryAdjustment(adjustment.id)"
                >
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </template>
            <span v-else>{{ formatSummaryAdjustmentLabel(adjustment) }}</span>
          </div>
          <div class="right">{{ formatSummaryAmount(getSummaryAdjustmentAmount(adjustment)) }}</div>
        </div>
        <div
          v-if="showInlineSummaryAdjustmentEditor && !isProjectReadOnly"
          class="row summary-adjustment-editor-row"
        >
          <div class="space"></div>
          <div class="left">
            <div class="summary-adjustment-editor-fields">
              <input
                type="text"
                :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentDraftTextEmpty }"
                v-model="summaryAdjustmentText"
                maxlength="80"
                placeholder="Bezeichnung eingeben"
              >
              <div class="summary-adjustment-signed-fields summary-adjustment-editor-signed-fields">
                <div
                  class="inline-input-with-suffix summary-adjustment-editor-value"
                  :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentDraftValueInvalid || isSummaryAdjustmentDraftDiscountOverLimit }"
                >
                  <input
                    type="number"
                    class="no-spinner"
                    :value="summaryAdjustmentPercentValue"
                    step="0.01"
                    placeholder="0"
                    @input="handleSummaryAdjustmentDraftPercentInput"
                  >
                  <span>%</span>
                </div>
                <div
                  class="inline-input-with-suffix summary-adjustment-editor-value"
                  :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentDraftValueInvalid || isSummaryAdjustmentDraftDiscountOverLimit }"
                >
                  <input
                    type="number"
                    class="no-spinner"
                    :value="summaryAdjustmentAmountValue"
                    step="0.01"
                    placeholder="0"
                    @input="handleSummaryAdjustmentDraftAmountInput"
                  >
                  <span>€</span>
                </div>
              </div>
              <div class="summary-adjustment-editor-actions">
                <button type="button" @click="createSummaryAdjustment">Anlegen</button>
                <button type="button" @click="closeInlineSummaryAdjustmentEditor">Abbrechen</button>
              </div>
            </div>
          </div>
          <div class="right"></div>
        </div>
        <div v-if="isInternalAdmin && !isProjectReadOnly" class="summary-adjustment-insert">
          <button
            type="button"
            class="summary-plus-button"
            title="Sonderrabatt hinzufügen"
            aria-label="Sonderrabatt hinzufügen"
            @click="openSummaryAdjustmentDialog"
          >
            +
          </button>
        </div>
        <div class="row">
          <div class="space"></div>
          <div class="left">Teuerungszuschlag ({{ formatPercentValue(tz) }}%)</div>
          <div class="right">{{ formatSummaryAmount(tzNetto) }}</div>
        </div>
        <div class="row" v-if="lieferBedingungen != null">
          <div class="space"></div>
          <div class="left">Lieferkosten ({{ getDeliveryConditionCartLabel() }})</div>
          <div class="right">{{ getLieferkostenDisplay() }}</div>
        </div>
        <div class="separator-row">

        </div>
        <div class="row">
          <div class="space"></div>
          <div class="left">Zwischensumme</div>
          <div class="right">{{ formatSummaryAmount(zwischenSum) }}</div>
        </div>
        <div class="row vat-row">
          <div class="space"></div>
          <div class="left">zzgl. 19% MwSt.</div>
          <div class="right">{{ formatSummaryAmount(mwst) }}</div>
        </div>
        <div class="total-row">

        </div>
        <div class="row">
          <div class="space"></div>
          <div class="left">Bruttobetrag</div>
          <div class="right">{{ formatSummaryAmount(brutto) }}</div>
        </div>
      </div>
      <div class="button-container">
        <button
          v-if="!isInternalAdmin"
          class="button-with-icon"
          :disabled="sendActionDisabled"
          :title="sendActionTitle"
          @click="confirmSendCartItems"
        >
          <img class="icon-in-button" :src="require('@/assets/dashboard/paper-plane-solid-dark-grey.svg')" alt="Icon" />
          Bestellung senden
        </button>
        <button
          v-if="!isInternalAdmin"
          class="button-with-icon"
          :disabled="requestSendDisabled"
          :title="requestSendTitle"
          @click="confirmSendInquiry"
        >
          <img class="icon-in-button" :src="require('@/assets/dashboard/message-solid-dark-grey.svg')" alt="Icon" />
          Anfrage senden
        </button>
        <button
          v-if="isInternalAdmin"
          class="button-with-icon"
          :disabled="sendActionDisabled"
          :title="sendActionTitle"
          @click="confirmSendOffer"
        >
          <img class="icon-in-button" :src="require('@/assets/dashboard/paper-plane-solid-dark-grey.svg')" alt="Icon" />
          Angebot senden
        </button>
        <button
          class="button-with-icon"
          :disabled="isProjectReadOnly"
          :title="isProjectReadOnly ? projectReadOnlyMessage : ''"
          @click="newPosition"
        >
          <img class="icon-in-button" :src="require('@/assets/dashboard/plus-solid-dark-grey.svg')" alt="Icon" />
          Neue Position
        </button>
        <button class="button-with-icon" @click="downloadRequestPdf">
          <img class="icon-in-button" :src="require('@/assets/dashboard/file-solid-dark-grey.svg')" alt="Icon" />
          PDF-Download
        </button>
        <button class="button-with-icon" @click="goToDashboard">
          <img class="icon-in-button" :src="require('@/assets/dashboard/house-solid-dark-grey.svg')" alt="Icon" />
          Zum Dashboard
        </button>
      </div>
    </div>
    <LegalFooter />
    <div v-if="showDialog" class="dialog">
      <div class="dialog-content">
        <p>{{ dialogMessage }}</p>
        <button @click="closeDialog">OK</button>
      </div>
    </div>
    <div v-if="isLoading" class="loading-overlay">
      <div class="spinner"></div>
    </div>
    <div v-if="showFreeTextDialog" class="free-text-dialog-overlay">
      <div class="free-text-dialog">
        <h3>Freitextposition</h3>
        <label for="freeTextPositionName">Raum</label>
        <input
          id="freeTextPositionName"
          type="text"
          v-model="freeTextPositionName"
          maxlength="7"
          placeholder="Raum"
        >
        <label for="freeTextContent">Freitext</label>
        <textarea
          id="freeTextContent"
          v-model="freeTextContent"
          rows="5"
          maxlength="500"
          placeholder="Freitext eingeben"
        ></textarea>
        <label for="freeTextTotal">LP/€</label>
        <div class="dialog-input-with-suffix">
          <input
            id="freeTextTotal"
            type="number"
            class="no-spinner"
            v-model.number="freeTextTotal"
            min="0"
            step="0.01"
            placeholder="0,00"
          >
          <span>€</span>
        </div>
        <div class="free-text-dialog-actions">
          <button @click="createFreeTextPosition">Anlegen</button>
          <button @click="closeFreeTextDialog">Abbrechen</button>
        </div>
      </div>
    </div>
    <div v-if="showSummaryAdjustmentDialog" class="free-text-dialog-overlay">
      <div class="free-text-dialog">
        <h3>Sonderrabatt</h3>
        <label for="summaryAdjustmentText">Text</label>
        <input
          id="summaryAdjustmentText"
          type="text"
          v-model="summaryAdjustmentText"
          maxlength="80"
          placeholder="Bezeichnung eingeben"
        >
        <label>Wert</label>
        <div class="summary-adjustment-signed-fields">
          <div
            class="dialog-input-with-suffix"
            :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentDraftValueInvalid || isSummaryAdjustmentDraftDiscountOverLimit }"
          >
            <input
              type="number"
              class="no-spinner"
              :value="summaryAdjustmentPercentValue"
              step="0.01"
              placeholder="0"
              @input="handleSummaryAdjustmentDraftPercentInput"
            >
            <span>%</span>
          </div>
          <div
            class="dialog-input-with-suffix"
            :class="{ 'summary-adjustment-required-input': isSummaryAdjustmentDraftValueInvalid || isSummaryAdjustmentDraftDiscountOverLimit }"
          >
            <input
              type="number"
              class="no-spinner"
              :value="summaryAdjustmentAmountValue"
              step="0.01"
              placeholder="0"
              @input="handleSummaryAdjustmentDraftAmountInput"
            >
            <span>€</span>
          </div>
        </div>
        <div class="free-text-dialog-actions">
          <button @click="createSummaryAdjustment">Anlegen</button>
          <button @click="closeSummaryAdjustmentDialog">Abbrechen</button>
        </div>
      </div>
    </div>
    <confirmation-dialog 
      v-if="showCartDeleteConfirmation" 
      :message="'Möchten Sie diesen Artikel wirklich aus dem Warenkorb entfernen?'" 
      @confirm="removeItemFromCart"
      @cancel="cancelRemoveItemFromCart"
    />
    <confirmation-dialog 
      v-if="showCartCopyConfirmation" 
      :message="'Möchten Sie diese Position wirklich kopieren?'"
      @confirm="copyItemToCart"
      @cancel="cancelCopyItemToCart"
    />
    <confirmation-dialog 
      v-if="showSendInquiryConfirmation" 
      :message="'Möchten Sie die Anfrage abschicken?'" 
      @confirm="sendInquiry"
      @cancel="cancelSendInquiry"
    />
    <confirmation-dialog 
      v-if="showSendCardItemsConfirmation" 
      :message="'Möchten Sie die Bestellung abschicken?'" 
      @confirm="sendCartItems"
      @cancel="cancelSendCartItems"
    />
    <confirmation-dialog
      v-if="showSendOfferConfirmation"
      :message="'Möchten Sie das Angebot abschicken?'"
      @confirm="sendOffer"
      @cancel="cancelSendOffer"
    />
  </template>
  
  <script>
  import api from '@/api/client';
  import { formatPercent, formatPrice } from '@/utils/numberFormat';
  import CartItem from './WarenkorbItemComponent.vue';
  import ConfirmationDialog from './ConfirmationDialog.vue';
  import LegalFooter from './LegalFooter.vue';

  const DISCOUNT_SLOT_COUNT = 12; // 0 = Grundrabatt, 1-11 = Rabattpositionen
  // Auf false setzen, falls die vorhandenen Dialoge später wieder aktiv werden sollen.
  const USE_INLINE_FREE_TEXT_ENTRY = true;
  const USE_INLINE_SUMMARY_ADJUSTMENT_ENTRY = true;

  // Artikel-IDs, die sich einen Rabatt-Index teilen sollen.
  // key = Artikel-ID, value = gewünschter Rabatt-Index
  const DISCOUNT_INDEX_BY_ARTICLE = {
    1: 1,   // 100/200
    2: 2,   // 300
    3: 2,   // 300 M
    4: 3,   // 400
    5: 3,   // 400
    6: 3,   // 400
    7: 3,   // 400
    8: 3,   // 400
    9: 3,   // 400
    10: 3,  // 450 -> 400 Rabattposition
    11: 3,  // 450 -> 400 Rabattposition
    12: 3,  // 450 -> 400 Rabattposition
    13: 4,  // 500
    14: 4,  // 500
    15: 5,  // 600
    16: 5,  // 650 -> 600 Rabattposition
    17: 6,  // 700
    18: 6,  // 700
    19: 6,  // 700
    20: 6,  // 700
    21: 7,  // 800
    22: 7,  // 800
    23: 7,  // 800
    24: 7,  // 800
    25: 9,  // P20
    26: 10, // P45
    27: 10, // P45
    28: 10, // P-Rollo -> P45 Rabattposition
    29: 9,  // P20
    30: 9,  // P20
  };

  
  export default {
    components: {
      CartItem,
      ConfirmationDialog,
      LegalFooter
    },
    inject: {
      appStage: {
        default: ''
      }
    },
    data() {
      return {
        company: null,
        loggedInUser: null,
        cartItems: [
          {
            pos: 1,
            articleId: 1,
            articleSearchText: "Kein Artikel im Warenkorb",
            items: [
              
            ],
            price: 0,
            productImage: ''
          },
          // Füge weitere Produktobjekte hier ein
        ],
        netto: 0.0,
        mwst: 0.0,
        zwischenSum: 0.0,
        brutto: 0.0,
        tz: 0.0,
        tzNetto: 0.0,
        showDialog: false, // Steuert die Anzeige des Dialogs
        dialogMessage: '', // Nachricht, die im Dialog angezeigt wird
        dialogGoToDashboard: false,
        isLoading: false,
        showSendInquiryConfirmation: false,
        showSendCardItemsConfirmation: false,
        showSendOfferConfirmation: false,
        showCartDeleteConfirmation: false,
        showCartCopyConfirmation: false,
        positionBezToDelete: null,
        copiedItem: null,
        selectedProject: null,
        discounts: [],
        lieferBedingungen: null,
        extraCharge: [],
        summaryAdjustments: [],
        summaryAdjustmentTotal: 0,
        showSummaryAdjustmentDialog: false,
        showInlineSummaryAdjustmentEditor: false,
        summaryAdjustmentText: '',
        summaryAdjustmentPercentValue: null,
        summaryAdjustmentAmountValue: null,
        showFreeTextDialog: false,
        showInlineFreeTextEditor: false,
        freeTextPositionName: '',
        freeTextContent: '',
        freeTextTotal: null,
        freeTextInsertAfterId: null,
        draggedCartItemId: null,
        dragOverCartItemId: null,
        dragOverPlacement: null,
        isSavingCartOrder: false,
        orderSaveMessage: '',
        orderSaveMessageTimer: null,
      };
    },
    computed: {
      isInternalAdmin() {
        const role = this.loggedInUser?.role;
        return role === 'internal_admin' || role === 'admin';
      },
      isProjectOrdered() {
        return !!(
          this.selectedProject?.has_order ||
          this.selectedProject?.hasOrder ||
          this.selectedProject?.order_sent_at ||
          this.selectedProject?.orderSentAt
        );
      },
      isOfferSent() {
        return !!(
          this.selectedProject?.has_offer ||
          this.selectedProject?.hasOffer ||
          this.selectedProject?.offer_sent_at ||
          this.selectedProject?.offerSentAt
        );
      },
      isRequestSent() {
        return !!(
          this.selectedProject?.has_request ||
          this.selectedProject?.hasRequest ||
          this.selectedProject?.request_sent_at ||
          this.selectedProject?.requestSentAt
        );
      },
      isProjectReadOnly() {
        return this.isProjectOrdered || (
          !this.isInternalAdmin && (
            this.isRequestSent ||
            !!this.selectedProject?.internal_admin_edited_at
          )
        );
      },
      projectReadOnlyMessage() {
        if (this.isProjectOrdered) {
          return 'Dieses Projekt wurde bereits bestellt und ist nicht mehr bearbeitbar.';
        }

        if (this.isRequestSent) {
          return 'Für dieses Projekt wurde bereits eine Anfrage gesendet. Es ist nicht mehr bearbeitbar.';
        }

        return `Dieses Projekt wurde von ${this.projectReadOnlyEditorLabel} bearbeitet und ist für Sie nur noch lesbar.`;
      },
      projectReadOnlyEditorLabel() {
        const editor = this.selectedProject?.internal_admin_edited_by;
        return this.userNameWithEmail(editor, 'Ansprechpartner/SB');
      },
      internalSperrgrundRaw() {
        return this.selectedProject?.shop_user?.sperrgrund_rtf || '';
      },
      internalSperrgrundText() {
        return this.decodeRtfText(this.internalSperrgrundRaw);
      },
      showInternalSperrgrund() {
        return this.isInternalAdmin && !!this.internalSperrgrundText;
      },
      showInternalProjectNote() {
        return this.isInternalAdmin && !!this.selectedProject;
      },
      showInternalProjectInfo() {
        return this.showInternalSperrgrund || this.showInternalProjectNote;
      },
      isBetaBuild() {
        const stage = (this.appStage || process.env.VUE_APP_STAGE || '').toUpperCase();
        const rootClassList = this.$root?.$el?.classList;

        return stage === 'BETA' || !!rootClassList?.contains('env-beta');
      },
      sendActionDisabled() {
        return this.isBetaBuild || this.isProjectOrdered;
      },
      sendActionTitle() {
        if (this.isProjectOrdered) {
          return this.projectReadOnlyMessage;
        }

        return this.isBetaBuild ? 'In der Beta deaktiviert' : '';
      },
      requestSendDisabled() {
        return this.sendActionDisabled || this.isOfferSent || this.isRequestSent;
      },
      requestSendTitle() {
        if (this.isOfferSent) {
          return 'Für dieses Projekt wurde bereits ein Angebot gesendet.';
        }

        if (this.isRequestSent) {
          return 'Für dieses Projekt wurde bereits eine Anfrage gesendet.';
        }

        return this.sendActionTitle;
      },
      hasPriceOnRequest() {
        return (this.cartItems || []).some(item => this.isCartItemPriceOnRequest(item));
      },
      isFreeTextDraftEmpty() {
        return !String(this.freeTextContent || '').trim();
      },
      isSummaryAdjustmentDraftTextEmpty() {
        return this.isSummaryAdjustmentTextEmpty(this.summaryAdjustmentText);
      },
      isSummaryAdjustmentDraftValueInvalid() {
        return this.isSummaryAdjustmentInvalid({
          percentValue: this.summaryAdjustmentPercentValue,
          amountValue: this.summaryAdjustmentAmountValue,
        });
      },
      isSummaryAdjustmentDraftDiscountOverLimit() {
        return this.wouldSummaryAdjustmentDraftExceedNetto();
      },
    },
    methods: {
      actorParams() {
        return {};
      },
      userNameWithEmail(user, fallbackName = 'Benutzer') {
        const name = String(user?.name || '').trim() || fallbackName;
        const email = String(user?.email || '').trim();
        return email ? `${name} (${email})` : name;
      },
      ensureProjectEditable() {
        if (!this.isProjectReadOnly) {
          return true;
        }

        this.dialogMessage = this.projectReadOnlyMessage;
        this.showDialog = true;
        return false;
      },
      ensureProjectSendable() {
        if (this.isProjectOrdered) {
          this.dialogMessage = this.projectReadOnlyMessage;
          this.showDialog = true;
          return false;
        }

        return !this.isBetaBuild;
      },
      ensureRequestSendable() {
        if (!this.ensureProjectSendable()) {
          return false;
        }

        if (this.isOfferSent) {
          this.dialogMessage = 'Für dieses Projekt wurde bereits ein Angebot gesendet.';
          this.showDialog = true;
          return false;
        }

        if (this.isRequestSent) {
          this.dialogMessage = 'Für dieses Projekt wurde bereits eine Anfrage gesendet.';
          this.showDialog = true;
          return false;
        }

        return true;
      },
      decodeRtfText(value) {
        const raw = String(value || '').trim();
        if (!raw) {
          return '';
        }

        if (!raw.startsWith('{\\rtf')) {
          return raw;
        }

        const rtfBody = this.removeRtfMetadataGroups(raw).replace(/\r?\n/g, ' ');

        return rtfBody
          .replace(/\\u(-?\d+)\??/g, (_, code) => {
            let charCode = Number(code);
            if (charCode < 0) {
              charCode += 65536;
            }
            return String.fromCharCode(charCode);
          })
          .replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
          .replace(/\\par\b/g, '\n')
          .replace(/\\line/g, '\n')
          .replace(/\\tab/g, '\t')
          .replace(/\\~/g, ' ')
          .replace(/\\-/g, '')
          .replace(/\\_/g, '-')
          .replace(/[{}]/g, '')
          .replace(/\\[a-zA-Z]+-?\d* ?/g, '')
          .replace(/\\[^a-zA-Z0-9]/g, '')
          .replace(/[ \t]+\n/g, '\n')
          .replace(/\n[ \t]+/g, '\n')
          .replace(/\n{3,}/g, '\n\n')
          .trim();
      },
      removeRtfMetadataGroups(rtf) {
        const skipControls = new Set([
          'fonttbl',
          'colortbl',
          'stylesheet',
          'info',
          'generator',
          'pict',
          'object',
          'datastore',
          'themedata',
          'colorschememapping'
        ]);
        let output = '';

        for (let i = 0; i < rtf.length; i += 1) {
          if (rtf[i] !== '{') {
            output += rtf[i];
            continue;
          }

          const groupControl = this.getRtfGroupControl(rtf, i + 1);
          if (!groupControl.ignorable && !skipControls.has(groupControl.name)) {
            output += rtf[i];
            continue;
          }

          let depth = 0;
          for (let j = i; j < rtf.length; j += 1) {
            if (rtf[j] === '\\') {
              j += 1;
              continue;
            }
            if (rtf[j] === '{') {
              depth += 1;
            } else if (rtf[j] === '}') {
              depth -= 1;
              if (depth === 0) {
                i = j;
                break;
              }
            }
          }
        }

        return output;
      },
      getRtfGroupControl(rtf, startIndex) {
        let index = startIndex;
        while (/\s/.test(rtf[index] || '')) {
          index += 1;
        }

        if (rtf[index] !== '\\') {
          return { name: '', ignorable: false };
        }

        index += 1;
        if (rtf[index] === '*') {
          return { name: '', ignorable: true };
        }

        const match = rtf.slice(index).match(/^[a-zA-Z]+/);
        return {
          name: match ? match[0].toLowerCase() : '',
          ignorable: false
        };
      },
      formatPercentValue(value) {
        return formatPercent(value);
      },
      formatSummaryAmount(value) {
        if (this.hasPriceOnRequest) {
          return 'Preis auf Anfrage';
        }
        return formatPrice(value);
      },
      formatSummaryAdjustmentLabel(adjustment) {
        const text = String(adjustment?.text || 'Sonderrabatt').trim() || 'Sonderrabatt';
        const config = this.getSummaryAdjustmentConfig(adjustment);

        if (config.calculationType !== 'percent' || config.value === null) {
          return text;
        }

        const percentValue = Number(config.value);
        if (!Number.isFinite(percentValue)) {
          return text;
        }

        const roundedPercent = Math.round(percentValue * 100) / 100;
        const fractionDigits = Number.isInteger(roundedPercent)
          ? 0
          : (Number.isInteger(roundedPercent * 10) ? 1 : 2);

        return `${text} (${formatPercent(roundedPercent, fractionDigits)}%)`;
      },
      parseOptionalNumber(value) {
        if (value === null || value === undefined || value === '') {
          return null;
        }

        const normalized = Number(String(value).replace(',', '.'));
        return Number.isFinite(normalized) ? normalized : null;
      },
      isSummaryAdjustmentTextEmpty(value) {
        return !String(value || '').trim();
      },
      isEmptyNumberInput(value) {
        return value === null || value === undefined || value === '';
      },
      getSummaryAdjustmentConfig(adjustment = {}) {
        const hasPercentProp = Object.prototype.hasOwnProperty.call(adjustment, 'percentValue');
        const hasAmountProp = Object.prototype.hasOwnProperty.call(adjustment, 'amountValue');
        const hasPercent = !this.isEmptyNumberInput(adjustment?.percentValue);
        const hasAmount = !this.isEmptyNumberInput(adjustment?.amountValue);

        if (hasPercentProp || hasAmountProp) {
          if (hasPercent === hasAmount) {
            return { calculationType: null, value: null, percentValue: null, amountValue: null };
          }

          if (hasPercent) {
            const value = this.parseOptionalNumber(adjustment.percentValue);
            return {
              calculationType: 'percent',
              value,
              percentValue: value,
              amountValue: null,
            };
          }

          const value = this.parseOptionalNumber(adjustment.amountValue);
          return {
            calculationType: 'amount',
            value,
            percentValue: null,
            amountValue: value,
          };
        }

        const value = this.parseOptionalNumber(adjustment?.value);
        if (value === null) {
          return { calculationType: null, value: null, percentValue: null, amountValue: null };
        }

        switch (adjustment?.calculationType) {
          case 'percent':
            return { calculationType: 'percent', value, percentValue: value, amountValue: null };
          case 'amount':
          case 'amount_add':
            return { calculationType: 'amount', value, percentValue: null, amountValue: value };
          case 'amount_subtract':
            return { calculationType: 'amount', value: -Math.abs(value), percentValue: null, amountValue: -Math.abs(value) };
          case 'percent_discount':
          default:
            return { calculationType: 'percent', value: -Math.abs(value), percentValue: -Math.abs(value), amountValue: null };
        }
      },
      isSummaryAdjustmentInvalid(adjustment) {
        const config = this.getSummaryAdjustmentConfig(adjustment);
        return !config.calculationType || config.value === null || config.value === 0;
      },
      handleSummaryAdjustmentPercentInput(adjustment, event) {
        adjustment.percentValue = event?.target?.value ?? '';
        if (!this.isEmptyNumberInput(adjustment.percentValue)) {
          adjustment.amountValue = null;
        }
        this.refreshCartSumPrice();
      },
      handleSummaryAdjustmentAmountInput(adjustment, event) {
        adjustment.amountValue = event?.target?.value ?? '';
        if (!this.isEmptyNumberInput(adjustment.amountValue)) {
          adjustment.percentValue = null;
        }
        this.refreshCartSumPrice();
      },
      handleSummaryAdjustmentDraftPercentInput(event) {
        this.summaryAdjustmentPercentValue = event?.target?.value ?? '';
        if (!this.isEmptyNumberInput(this.summaryAdjustmentPercentValue)) {
          this.summaryAdjustmentAmountValue = null;
        }
      },
      handleSummaryAdjustmentDraftAmountInput(event) {
        this.summaryAdjustmentAmountValue = event?.target?.value ?? '';
        if (!this.isEmptyNumberInput(this.summaryAdjustmentAmountValue)) {
          this.summaryAdjustmentPercentValue = null;
        }
      },
      isDiscountSummaryAdjustment(adjustment) {
        return this.getSummaryAdjustmentAmount(adjustment) < 0;
      },
      getSummaryDiscountCents(adjustment, baseNetto = this.netto) {
        const amount = this.getSummaryAdjustmentAmount(adjustment, baseNetto);
        return amount < 0 ? Math.round(Math.abs(amount) * 100) : 0;
      },
      getSummaryDiscountTotalCents(adjustments = this.summaryAdjustments, baseNetto = this.netto) {
        return (adjustments || []).reduce((sum, adjustment) => (
          sum + this.getSummaryDiscountCents(adjustment, baseNetto)
        ), 0);
      },
      summaryDiscountsExceedNetto(adjustments = this.summaryAdjustments, baseNetto = this.netto) {
        const nettoCents = Math.max(Math.round((Number(baseNetto) || 0) * 100), 0);
        return this.getSummaryDiscountTotalCents(adjustments, baseNetto) > nettoCents;
      },
      ensureSummaryDiscountLimit() {
        if (!this.summaryDiscountsExceedNetto(this.summaryAdjustments, this.netto)) {
          return true;
        }

        this.dialogMessage = 'Die Sonderrabatte dürfen zusammen den Nettowert nicht überschreiten.';
        this.showDialog = true;
        return false;
      },
      isSummaryAdjustmentDiscountOverLimit(adjustment) {
        return this.isDiscountSummaryAdjustment(adjustment)
          && this.summaryDiscountsExceedNetto(this.summaryAdjustments, this.netto);
      },
      wouldSummaryAdjustmentDraftExceedNetto() {
        const draftAdjustment = {
          percentValue: this.summaryAdjustmentPercentValue,
          amountValue: this.summaryAdjustmentAmountValue,
        };
        const draftAmount = this.getSummaryAdjustmentAmount(draftAdjustment);

        if (this.isSummaryAdjustmentInvalid(draftAdjustment) || draftAmount >= 0) {
          return false;
        }

        return this.summaryDiscountsExceedNetto([
          ...(this.summaryAdjustments || []),
          draftAdjustment,
        ], this.netto);
      },
      getLimitedSummaryAdjustmentTotalCents(adjustments = this.summaryAdjustments, baseNetto = this.netto) {
        const nettoCents = Math.max(Math.round((Number(baseNetto) || 0) * 100), 0);
        let surchargeCents = 0;
        let discountCents = 0;

        (adjustments || []).forEach(adjustment => {
          const amountCents = Math.round(this.getSummaryAdjustmentAmount(adjustment, baseNetto) * 100);
          if (amountCents < 0) {
            discountCents += amountCents;
          } else {
            surchargeCents += amountCents;
          }
        });

        return surchargeCents + Math.max(discountCents, -nettoCents);
      },
      hasManualTotalPrice(item) {
        const manualTotal = this.parseOptionalNumber(item?.manualTotalPrice);
        if (manualTotal === null) {
          return false;
        }

        const price = Number(item?.price);
        const hasListPrice = Number.isFinite(price) && price > 0;
        return !hasListPrice && manualTotal >= 0;
      },
      isAlternativePosition(item) {
        const value = item?.isAlternativePosition ?? item?.alternativePosition;
        return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
      },
      isCartItemPriceOnRequest(item) {
        if (this.isAlternativePosition(item)) {
          return false;
        }

        const price = Number(item?.price);
        return (!Number.isFinite(price) || price <= 0) && !this.hasManualTotalPrice(item);
      },
      getCartItemLineTotal(item) {
        if (this.isAlternativePosition(item)) {
          return null;
        }

        const manualTotal = this.parseOptionalNumber(item?.manualTotalPrice);
        const price = Number(item?.price);
        const hasListPrice = Number.isFinite(price) && price > 0;
        if (manualTotal !== null && !hasListPrice && manualTotal >= 0) {
          return manualTotal;
        }

        if (!hasListPrice) {
          return null;
        }

        const qty = Number(item?.amount) || 0;
        const discount = Number(item?.discount) || 0;
        return price * qty * (1 - discount / 100);
      },
      getSummaryAdjustmentAmount(adjustment, baseNetto = this.netto) {
        const config = this.getSummaryAdjustmentConfig(adjustment);
        if (!config.calculationType || config.value === null) {
          return 0;
        }

        const nettoValue = Number(baseNetto) || 0;

        switch (config.calculationType) {
          case 'amount':
            return config.value;
          case 'percent':
            return nettoValue * config.value / 100;
          default:
            return 0;
        }
      },
      getMailSummaryAdjustments() {
        return (this.summaryAdjustments || []).map(adjustment => {
          const config = this.getSummaryAdjustmentConfig(adjustment);

          return {
            text: adjustment.text,
            calculationType: config.calculationType,
            value: config.value,
            percentValue: config.percentValue,
            amountValue: config.amountValue,
            amount: this.getSummaryAdjustmentAmount(adjustment),
          };
        });
      },
      buildCartItemJson(item) {
        let config = {};

        try {
          config = item?.rawJson ? JSON.parse(item.rawJson) : {};
        } catch (error) {
          config = {};
        }

        const manualListPrice = this.parseOptionalNumber(item?.manualListPrice);
        const originalPrice = this.parseOptionalNumber(item?.originalPrice);
        const currentPrice = this.parseOptionalNumber(item?.price);
        const effectivePrice = manualListPrice ?? currentPrice ?? originalPrice ?? 0;

        config.articleId = item?.articleId ?? config.articleId ?? null;
        config.articleDiscountPosition = item?.articleDiscountPosition ?? config.articleDiscountPosition ?? null;
        config.articleSearchText = item?.isFreeText ? '' : (item?.articleSearchText ?? config.articleSearchText ?? '');
        config.priorityFolders = item?.priorityFolders ?? config.priorityFolders ?? null;
        config.items = item?.items ?? config.items ?? [];
        config.price = effectivePrice;
        if (manualListPrice !== null || config.originalPrice !== undefined) {
          config.originalPrice = originalPrice ?? 0;
        }
        config.manualListPrice = manualListPrice;
        config.manualDiscount = this.parseOptionalNumber(item?.manualDiscount);
        config.manualTotalPrice = this.parseOptionalNumber(item?.manualTotalPrice);
        config.isFreeText = !!item?.isFreeText;
        config.isAlternativePosition = this.isAlternativePosition(item);
        config.type = item?.isFreeText ? 'free_text' : (config.type ?? null);
        config.sortPosition = this.parseOptionalNumber(item?.sortPosition) ?? item?.pos ?? config.sortPosition ?? null;

        return JSON.stringify(config);
      },
      async persistCartItem(item) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        if (!item?.id) {
          return;
        }

        const artikelJson = this.buildCartItemJson(item);
        item.rawJson = artikelJson;

        await api.put(`/api/gespeicherteartikel/${item.id}`, {
          position_bezeichnung: item.positionBez,
          artikel_json: artikelJson,
          anzahl: item.amount,
          notizen: item.notes,
          projekt_id: this.selectedProject?.id ?? null,
          ...this.actorParams(),
        });
      },
      getLieferkostenDisplay() {
        if (this.hasPriceOnRequest) {
          return 'Preis auf Anfrage';
        }
        if (Number(this.lieferBedingungen?.liefer_kosten) > 0) {
          return formatPrice(this.lieferBedingungen.liefer_kosten);
        }
        return `${this.lieferBedingungen?.liefer_text ?? ''}`;
      },
      getDeliveryConditionCartLabel() {
        return this.lieferBedingungen?.l_bed_warenkorb_bezeichnung
          || this.lieferBedingungen?.l_bed_bezeichnung
          || '';
      },
      getDiscountIndexForArticle(articleId, articleDiscountPosition = null) {
        const numericId = Number(articleId);
        const mapped = DISCOUNT_INDEX_BY_ARTICLE[numericId];
        if (mapped != null) {
          return mapped;
        }

        const configuredPosition = Number(articleDiscountPosition);
        if (Number.isInteger(configuredPosition) && configuredPosition >= 0 && configuredPosition < this.discounts.length) {
          return configuredPosition;
        }

        if (Number.isNaN(numericId)) {
          return 0;
        }
        return (numericId >= 0 && numericId < this.discounts.length) ? numericId : 0;
      },
      updateProductImage({ id, newPath }) {
        const item = this.cartItems.find(c => c.id === id);
        if (item) {
          item.productImage = newPath;
        }
      },
      async scrollToItem(targetId = null) {
        await this.$nextTick();
        const itemComponents = Array.isArray(this.$refs.cartItemsRefs) ? this.$refs.cartItemsRefs : [];
        if (!itemComponents.length) return;

        const targetComponent = targetId != null
          ? itemComponents.find(c => c.id === targetId)
          : null;

        const component = targetComponent || itemComponents[itemComponents.length - 1];
        if (!component || !component.$el) return;

        if (typeof component.$el.scrollIntoView === 'function') {
          component.$el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        }

        if (typeof component.flashHighlight === 'function') {
          component.flashHighlight();
        }
      },
      sleep(ms = 150) {
        return new Promise(resolve => setTimeout(resolve, ms));
      },
      async updateProjectNotes() {
        if (!this.ensureProjectEditable()) {
          return;
        }

        if (this.selectedProject?.id) {
          try {
            await api.put(`/api/projekte/${this.selectedProject.id}`, {
              notizen: this.selectedProject.notizen ?? '',
              ...this.actorParams(),
            });
            console.log('Notizen wurden erfolgreich aktualisiert.');
          } catch (error) {
            console.error('Fehler beim Aktualisieren der Notizen:', error);
          }
        }
      },
      async updateInternalProjectNote() {
        if (!this.isInternalAdmin || !this.selectedProject?.id) {
          return;
        }

        try {
          const response = await api.put(`/api/projekte/${this.selectedProject.id}`, {
            internal_note: this.selectedProject.internal_note ?? '',
            ...this.actorParams(),
          });

          this.selectedProject = {
            ...this.selectedProject,
            internal_note: response.data?.internal_note ?? '',
          };

          if (this.$parent?.setSelectedProject) {
            this.$parent.setSelectedProject(this.selectedProject);
          }
        } catch (error) {
          console.error('Fehler beim Aktualisieren der internen Notiz:', error);
        }
      },
      async getProjectDeliveryConditions() {
        try {
          if (this.company == null) {
            this.company = this.$parent.getCompany();
          }
          const response = await api.get(`/api/delivery_conditions/${this.company.delivery_condition_id}`);
          if (response.data && response.data.length > 0) {
            this.lieferBedingungen = {
              berechnung: response.data[0].berechnung,
              l_bed_bezeichnung: response.data[0].l_bed_bezeichnung,
              l_bed_warenkorb_bezeichnung: response.data[0].l_bed_warenkorb_bezeichnung,
              l_bed_nummer: response.data[0].l_bed_nummer,
              liefer_kosten: 0.0,
              liefer_text: "",
              prozent_satz: 0.0,
              mindest_satz: 0.0,
              maximal_satz: 0.0,
              frei_ab: 0.0,
              berechnung_vorhanden: false,
            };

            if (response.data[0].berechnung == null) {
              this.lieferBedingungen.liefer_text = "-";
            }
            else {
              var tmpLieferText = response.data[0].berechnung.split("t:");
              if (tmpLieferText.length > 1) {
                this.lieferBedingungen.liefer_text = tmpLieferText[1];
              } 
              else {
                // Sätze definieren
                var tmpBerechnung = response.data[0].berechnung.split(";");
                this.lieferBedingungen.berechnung_vorhanden = true;

                for (var i = 0; i < tmpBerechnung.length; ++i) {
                  if (tmpBerechnung[i].includes("p:")) {
                    this.lieferBedingungen.prozent_satz = parseFloat(tmpBerechnung[i].split(":")[1]);
                  }
                  else if(tmpBerechnung[i].includes("mi:")) {
                    this.lieferBedingungen.mindest_satz = parseFloat(tmpBerechnung[i].split(":")[1]);
                  }
                  else if(tmpBerechnung[i].includes("ma:")) {
                    this.lieferBedingungen.maximal_satz = parseFloat(tmpBerechnung[i].split(":")[1]);
                  }
                  else if(tmpBerechnung[i].includes("fr:")) {
                    this.lieferBedingungen.frei_ab = parseFloat(tmpBerechnung[i].split(":")[1]);
                  }
                }
              }
            }
          }
          this.refreshCartSumPrice(); // Aktualisiere die Summe, nachdem der Rabatt geladen wurde
        } catch (error) {
          console.error('Fehler beim Abrufen der Lieferbedingungen:', error);
        }
      },
      async getCartExtraCharge() {
        // Stelle sicher, dass die Firmendaten vorhanden sind, bevor eine Anfrage gestellt wird.
        if (!this.company) {
          this.company = this.$parent?.getCompany ? this.$parent.getCompany() : null;
        }
        if (!this.company?.id) {
          console.warn('Keine Firma geladen – Teuerungszuschlag kann nicht abgerufen werden.');
          this.extraCharge = [];
          return;
        }
        const response = await api.get(`/api/discounts/unternehmen/${this.company.id}`);
        this.extraCharge = response.data.filter(item => item.type == 1);

        for (let i = 0; i < this.extraCharge.length; ++i) {
          // 10 Teuerungsaufschlag
          if (this.extraCharge[i].kuerzel == "TZ") {
            // Backend liefert hier teils Strings (ggf. mit Komma). Immer in Number wandeln,
            // damit toFixed im Template nicht auf einem String aufgerufen wird.
            const parsedTz = parseFloat(String(this.extraCharge[i].rabatt).replace(',', '.'));
            this.tz = isNaN(parsedTz) ? 0.0 : parsedTz;
          }
        }
      },
      async getProjectDiscount() {
        // Stelle sicher, dass die Firmendaten vorhanden sind, bevor eine Anfrage gestellt wird.
        if (!this.company) {
          this.company = this.$parent?.getCompany ? this.$parent.getCompany() : null;
        }
        if (!this.company?.id) {
          console.warn('Keine Firma geladen – Projektrabatt wird auf 0 gesetzt.');
          this.discounts = Array(DISCOUNT_SLOT_COUNT).fill(0.0);
          this.cartItems.forEach(cartItem => {
            cartItem.discount = cartItem.manualDiscount !== null && cartItem.manualDiscount !== undefined
              ? Number(cartItem.manualDiscount) || 0
              : 0.0;
          });
          return;
        }
        try {
          const response = await api.get(`/api/discounts/unternehmen/${this.company.id}`);
          // Nur die discounts für die Beschläge
          response.data = response.data.filter(item => item.type == 0);
          this.discounts = [];
          if (response.data && response.data.length > 0) {
            response.data.forEach(dataSet => {
              const position = Number(dataSet.position);
              if (Number.isInteger(position) && position >= 0) {
                this.discounts[position] = dataSet.rabatt;
              }
            });
            for (let i = 0; i < DISCOUNT_SLOT_COUNT; ++i) {
              if (this.discounts[i] === undefined) {
                this.discounts[i] = 0.0;
              }
            }
          } else {
            for(let i = 0; i < DISCOUNT_SLOT_COUNT; ++i) {
              this.discounts.push(0.0); // Fallback auf 0, falls kein Rabatt gefunden wurde
            } 
          }
          this.refreshCartSumPrice(); // Aktualisiere die Summe, nachdem der Rabatt geladen wurde
        } catch (error) {
          console.error('Fehler beim Abrufen des Projektrabatts:', error);
          this.discounts = [];
          for(let i = 0; i < DISCOUNT_SLOT_COUNT; ++i) {
              this.discounts.push(0.0); // Fallback auf 0, falls kein Rabatt gefunden wurde
          } 
        }
        this.cartItems.forEach(cartItem => {
          if (cartItem.isFreeText) {
            cartItem.discount = cartItem.manualDiscount !== null && cartItem.manualDiscount !== undefined
              ? Number(cartItem.manualDiscount) || 0
              : 0.0;
            return;
          }
          if (cartItem.manualDiscount !== null && cartItem.manualDiscount !== undefined) {
            cartItem.discount = Number(cartItem.manualDiscount) || 0;
            return;
          }
          const idx = this.getDiscountIndexForArticle(cartItem.articleId, cartItem.articleDiscountPosition);
          cartItem.discount = this.discounts[idx] ?? 0.0;
        });
      },
      parseSMassDisplayValue(displayValue) {
        if (!displayValue || typeof displayValue !== 'string') {
          return { s: null, groupText: null, smk: null, fa: null };
        }

        const text = displayValue.trim();
        const re = /^\s*(\d+)\s*mm(?:\s*\(([^)]+)\))?(?:\s+(\d+)\s*mm)?(?:\s+(\d+)\s*mm)?\s*$/i;
        const m = text.match(re);

        if (!m) {
          return { s: null, groupText: null, smk: null, fa: null };
        }

        const [, s, groupText, smk, fa] = m;

        return {
          s: s != null ? Number(s) : null,
          groupText: groupText || null,
          smk: smk != null ? Number(smk) : null,
          fa: fa != null ? Number(fa) : null,
        };
      },
      parseFluegelDisplayValue(displayValue) {
        const nums = Array.from(String(displayValue || '').matchAll(/(\d+(?:[.,]\d+)?)/g)).map(m => m[1].replace(',', '.'));
        if (nums.length === 0) {
          return { left: null, right: null, height: null };
        }

        const left = nums[0] ?? null;
        const right = nums.length >= 3 ? nums[2] : (nums[1] ?? null);
        const height = nums.length >= 2 ? nums[1] : null;

        return { left, right, height };
      },
      normalizeDimensionValue(val) {
        return (val ?? '').toString().replace(/mm/ig, '').replace(/,/g, '.').trim();
      },
      getHeightFromItems(items = []) {
        const dimItem = (items || []).find(it => {
          const n = (it.name || '').toLowerCase();
          return n.includes('maß') && !n.includes('s-maß');
        });
        if (dimItem && typeof dimItem.value === 'string') {
          const parsedDim = this.parseFluegelDisplayValue(dimItem.value);
          if (parsedDim.height) {
            return this.normalizeDimensionValue(parsedDim.height);
          }
          if (parsedDim.right) {
            return this.normalizeDimensionValue(parsedDim.right);
          }
        }
        return null;
      },
      getCartItemRawValue(item) {
        return item.value != null && String(item.value).trim() !== ''
          ? item.value
          : (item.emptyValue != null ? item.emptyValue : item.value);
      },
      shouldHideCartItem(item) {
        const rawValue = this.getCartItemRawValue(item);
        const normalizedValue = String(rawValue ?? '').trim().toLowerCase();
        return normalizedValue === '' || normalizedValue === 'ohne';
      },
      formatGriffkaempferHoeheValue(item, rawValue) {
        const name = (item?.name || '').toLowerCase();
        const value = String(rawValue || '').trim();

        if (!name.includes('kämpfer') || !name.includes('höhe') || !value || value.toLowerCase().includes('v. uk ram')) {
          return null;
        }

        const numericValue = Number(value.replace(/mm/ig, '').replace(',', '.').trim());
        if (Number.isNaN(numericValue)) {
          return null;
        }

        return `${this.normalizeDimensionValue(value)}mm v. UK RAM`;
      },
      formatEndrasterwunschhoeheValue(item, rawValue) {
        const name = (item?.name || '').toLowerCase();
        const value = String(rawValue || '').trim();

        if (name !== 'endrasterwunschhöhe' || !value || value.toLowerCase().includes('v. unten')) {
          return null;
        }

        const numericValue = Number(value.replace(/mm/ig, '').replace(',', '.').trim());
        return Number.isNaN(numericValue) ? null : `${this.normalizeDimensionValue(value)}mm v. unten`;
      },
      formatLabeledLineValue(rawValue) {
        if (typeof rawValue !== 'string' || !/(^|\s)Seite\s+\d+\s*:/i.test(rawValue)) {
          return null;
        }

        const parts = rawValue
          .split(/\s+\/\s+|\n+/)
          .map(part => part.trim())
          .filter(Boolean);

        if (parts.length < 2 || !parts.every(part => /^Seite\s+\d+\s*:/i.test(part))) {
          return null;
        }

        return parts.join('\n');
      },
      getFormattedCartItemValue(item, siblingItems = []) {
        const rawValue = this.getCartItemRawValue(item);

        if (item.name === 'S-Maß' && typeof rawValue === 'string') {
          const parsed = this.parseSMassDisplayValue(rawValue);

          if (parsed?.smk != null && parsed?.groupText) {
            const groupNumberMatch = parsed.groupText.match(/\d+/);
            const groupNumber = groupNumberMatch ? groupNumberMatch[0] : parsed.groupText;
            return `S-Maß (Klammer) ${parsed.smk}mm aus Gr. ${groupNumber}`;
          }
        }

        const formattedLabeledLineValue = this.formatLabeledLineValue(rawValue);
        if (formattedLabeledLineValue) {
          return formattedLabeledLineValue;
        }

        const formattedGriffkaempferHoehe = this.formatGriffkaempferHoeheValue(item, rawValue);
        if (formattedGriffkaempferHoehe) {
          return formattedGriffkaempferHoehe;
        }

        const formattedEndrasterwunschhoehe = this.formatEndrasterwunschhoeheValue(item, rawValue);
        if (formattedEndrasterwunschhoehe) {
          return formattedEndrasterwunschhoehe;
        }

        if (item.name === 'Flügel' && typeof rawValue === 'string') {
          const parsed = this.parseFluegelDisplayValue(rawValue);
          const left = parsed.left ? this.normalizeDimensionValue(parsed.left) : null;
          const right = parsed.right ? this.normalizeDimensionValue(parsed.right) : null;
          const heightRaw = this.getHeightFromItems(siblingItems) || parsed.height;
          const height = heightRaw ? this.normalizeDimensionValue(heightRaw) : null;

          if (left && right && height) {
            return `Flügel 1: ${left}mm x ${height}mm
Flügel 2: ${right}mm x ${height}mm`;
          }
          if (left && height) {
            return `Flügel 1: ${left}mm x ${height}mm`;
          }
          if (left && right) {
            return `Flügel 1: ${left}mm
Flügel 2: ${right}mm`;
          }
        }

        return rawValue;
      },
      getMailCartItems({ includeAlternativePositions = true } = {}) {
        return this.cartItems
          .filter(cartItem => includeAlternativePositions || !this.isAlternativePosition(cartItem))
          .map(cartItem => ({
            ...cartItem,
            productImage: cartItem.productImage === 'pfad/zum/bild1.jpg' ? '' : (cartItem.productImage || ''),
            items: (cartItem.items || [])
              .filter(item => !this.shouldHideCartItem(item))
              .map(item => ({
                ...item,
                value: this.getFormattedCartItemValue(item, cartItem.items || [])
              }))
          }));
      },
      resolveProjectCompany() {
        const projectCompany = this.selectedProject?.unternehmen || null;
        if (this.company && projectCompany) {
          return {
            ...this.company,
            ...projectCompany,
          };
        }

        return projectCompany || this.company || {};
      },
      resolveProjectMailUser(projectCompany) {
        const projectUser = this.selectedProject?.shop_user || null;
        if (projectUser?.email) {
          return projectUser;
        }

        if (this.selectedProject && !this.selectedProject.shop_user_id && !projectUser) {
          const fallbackEmail = this.companyInfoEmail(projectCompany);
          if (fallbackEmail) {
            return {
              id: null,
              name: projectCompany?.name || 'Info',
              email: fallbackEmail,
              tel: '',
              mobil: '',
              unternehmen_id: projectCompany?.id || this.selectedProject?.unternehmen_id || null,
            };
          }
        }

        return projectUser || this.loggedInUser || {};
      },
      companyInfoEmail(company) {
        for (const field of ['email_ab', 'email_rg']) {
          const domain = this.extractEmailDomain(company?.[field]);
          if (domain) {
            return `info@${domain}`;
          }
        }

        return '';
      },
      extractEmailDomain(value) {
        const match = String(value || '').match(/[A-Z0-9._%+-]+@([A-Z0-9.-]+\.[A-Z]{2,})/i);
        return match?.[1]?.replace(/\.+$/, '').toLowerCase() || '';
      },
      buildCartPdfPayload({ includeAlternativePositions = true } = {}) {
        this.refreshCartSumPrice();
        const projectCompany = this.resolveProjectCompany();
        const projectUser = this.resolveProjectMailUser(projectCompany);

        return {
          items: this.getMailCartItems({ includeAlternativePositions }),
          company: [projectCompany],
          user: [projectUser],
          summary_adjustments: this.getMailSummaryAdjustments(),
          summary_adjustment_total: this.summaryAdjustmentTotal,
          tz: this.tz,
          tzNetto: this.tzNetto,
          netto: this.netto,
          mwst: this.mwst,
          zwischenSum: this.zwischenSum,
          brutto: this.brutto,
          lieferkosten_bez: this.getDeliveryConditionCartLabel() || null,
          lieferkosten_text: this.lieferBedingungen?.liefer_text ?? null,
          lieferkosten_betrag: this.lieferBedingungen?.liefer_kosten ?? null,
          notizen: this.selectedProject?.notizen ?? '',
          project_name: this.selectedProject?.name ?? '',
          projekt_id: this.selectedProject?.id ?? null,
        };
      },
      buildRequestPdfFilename() {
        const projectName = (this.selectedProject?.name || 'Projekt')
          .replace(/[\\/:*?"<>|]+/g, '_')
          .replace(/\s+/g, ' ')
          .replace(/_+/g, '_')
          .replace(/^[ ._]+|[ ._]+$/g, '');

        return `${projectName || 'Projekt'}.pdf`;
      },
      downloadBlob(blob, filename) {
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
      },
      extractFilenameFromDisposition(dispositionHeader) {
        if (!dispositionHeader) {
          return null;
        }

        const utf8Match = dispositionHeader.match(/filename\*=UTF-8''([^;]+)/i);
        if (utf8Match?.[1]) {
          return decodeURIComponent(utf8Match[1]);
        }

        const asciiMatch = dispositionHeader.match(/filename="([^"]+)"/i) || dispositionHeader.match(/filename=([^;]+)/i);
        if (!asciiMatch?.[1]) {
          return null;
        }

        return asciiMatch[1].trim();
      },
      removeItemFromCart() {
        if (!this.ensureProjectEditable()) {
          this.showCartDeleteConfirmation = false;
          return;
        }

        var actualCartItem = this.cartItems.find(item => item.id === this.positionBezToDelete);
        this.showCartDeleteConfirmation = false;
        this.cartItems = this.cartItems.filter(item => item.id !== this.positionBezToDelete);
        this.refreshCartItemPostion();
        this.$nextTick(() => {
          this.$parent.deletePositionWithID(actualCartItem.id);
          this.refreshCartSumPrice();
        });
      },
      refreshCartItemPostion() {
        for(let i = 0; i < this.cartItems.length; ++i) {
          this.cartItems[i].pos = i + 1;
          this.cartItems[i].sortPosition = i + 1;
        }
      },
      sortCartItemsByPosition() {
        this.cartItems.sort((a, b) => {
          const sortA = this.parseOptionalNumber(a.sortPosition) ?? Number.MAX_SAFE_INTEGER;
          const sortB = this.parseOptionalNumber(b.sortPosition) ?? Number.MAX_SAFE_INTEGER;
          if (sortA !== sortB) {
            return sortA - sortB;
          }
          return (Number(a.id) || 0) - (Number(b.id) || 0);
        });
      },
      moveCartItemAfter(itemId, afterItemId) {
        const itemIndex = this.cartItems.findIndex(item => item.id === itemId);
        if (itemIndex < 0) {
          return;
        }

        const [item] = this.cartItems.splice(itemIndex, 1);
        const targetIndex = afterItemId == null
          ? this.cartItems.length - 1
          : this.cartItems.findIndex(cartItem => cartItem.id === afterItemId);

        this.cartItems.splice(targetIndex >= 0 ? targetIndex + 1 : this.cartItems.length, 0, item);
        this.refreshCartItemPostion();
      },
      async persistCartOrder() {
        if (!this.ensureProjectEditable()) {
          return;
        }

        await Promise.all(this.cartItems.map(item => this.persistCartItem(item)));
      },
      startCartItemDrag({ id, event }) {
        if (this.isSavingCartOrder || !this.ensureProjectEditable()) {
          return;
        }

        this.draggedCartItemId = id;
        this.dragOverCartItemId = null;
        this.dragOverPlacement = null;

        if (event?.dataTransfer) {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', String(id));
        }
      },
      updateCartItemDropTarget({ id, placement }) {
        if (this.draggedCartItemId == null || id === this.draggedCartItemId) {
          this.dragOverCartItemId = null;
          this.dragOverPlacement = null;
          return;
        }

        this.dragOverCartItemId = id;
        this.dragOverPlacement = placement === 'after' ? 'after' : 'before';
      },
      async dropCartItem({ id, placement }) {
        const draggedId = this.draggedCartItemId;
        this.endCartItemDrag();

        if (draggedId == null || draggedId === id) {
          return;
        }

        await this.reorderCartItem(draggedId, id, placement);
      },
      endCartItemDrag() {
        this.draggedCartItemId = null;
        this.dragOverCartItemId = null;
        this.dragOverPlacement = null;
      },
      async moveCartItemByDirection({ id, direction }) {
        if (this.isSavingCartOrder) {
          return;
        }

        const currentIndex = this.cartItems.findIndex(item => item.id === id);
        if (currentIndex < 0) {
          return;
        }

        const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= this.cartItems.length) {
          return;
        }

        const targetItem = this.cartItems[targetIndex];
        await this.reorderCartItem(id, targetItem.id, direction === 'up' ? 'before' : 'after');
      },
      async reorderCartItem(itemId, targetItemId, placement = 'before') {
        if (this.isSavingCartOrder || !this.ensureProjectEditable() || itemId === targetItemId) {
          return;
        }

        const previousOrder = this.cartItems.slice();
        const sourceIndex = this.cartItems.findIndex(item => item.id === itemId);
        if (sourceIndex < 0) {
          return;
        }

        const [movedItem] = this.cartItems.splice(sourceIndex, 1);
        const targetIndex = this.cartItems.findIndex(item => item.id === targetItemId);
        if (targetIndex < 0) {
          this.cartItems = previousOrder;
          return;
        }

        const insertionIndex = targetIndex + (placement === 'after' ? 1 : 0);
        this.cartItems.splice(insertionIndex, 0, movedItem);
        this.refreshCartItemPostion();
        if (this.orderSaveMessageTimer) {
          clearTimeout(this.orderSaveMessageTimer);
          this.orderSaveMessageTimer = null;
        }
        this.isSavingCartOrder = true;
        this.orderSaveMessage = 'Reihenfolge wird gespeichert …';

        try {
          await this.persistCartOrder();
          this.showOrderSaveMessage('Reihenfolge gespeichert');
        } catch (error) {
          console.error('Fehler beim Speichern der Warenkorbreihenfolge:', error);
          this.cartItems = previousOrder;
          this.refreshCartItemPostion();
          await Promise.allSettled(this.cartItems.map(item => this.persistCartItem(item)));
          this.orderSaveMessage = '';
          this.dialogMessage = 'Die Reihenfolge konnte nicht gespeichert werden.';
          this.showDialog = true;
        } finally {
          this.isSavingCartOrder = false;
        }
      },
      showOrderSaveMessage(message) {
        if (this.orderSaveMessageTimer) {
          clearTimeout(this.orderSaveMessageTimer);
        }

        this.orderSaveMessage = message;
        this.orderSaveMessageTimer = setTimeout(() => {
          this.orderSaveMessage = '';
          this.orderSaveMessageTimer = null;
        }, 1800);
      },
      confirmSendInquiry() {
        if (!this.ensureRequestSendable()) {
          return;
        }

        this.showSendInquiryConfirmation = true;
      },
      confirmSendCartItems() {
        if (!this.ensureProjectSendable()) {
          return;
        }

        this.showSendCardItemsConfirmation = true;
      },
      confirmSendOffer() {
        if (!this.ensureProjectSendable()) {
          return;
        }

        this.showSendOfferConfirmation = true;
      },
      confirmCopyItemToCart(copyItm) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        this.copiedItem = copyItm;
        this.showCartCopyConfirmation = true;
      },
      confirmRemoveItemFromCart(positionId) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        this.positionBezToDelete = positionId;
        this.showCartDeleteConfirmation = true;
      },
      cancelCopyItemToCart() {
        this.showCartCopyConfirmation = false;
      },
      cancelRemoveItemFromCart() {
        this.showCartDeleteConfirmation = false;
      },
      cancelSendInquiry() {
        this.showSendInquiryConfirmation = false;
      },
      cancelSendCartItems() {
        this.showSendCardItemsConfirmation = false;
      },
      cancelSendOffer() {
        this.showSendOfferConfirmation = false;
      },
      async copyItemToCart() {
        if (!this.ensureProjectEditable()) {
          this.showCartCopyConfirmation = false;
          return;
        }

        this.showCartCopyConfirmation = false;
        if (this.copiedItem?.isFreeText) {
          await this.copyFreeTextPosition(this.copiedItem);
          return;
        }

        //this.cartItems.push(this.copiedItem);
        //this.refreshCartSumPrice();
        //await this.$parent.saveCopiedItemToMain(this.copiedItem);
        this.$parent.setLoading(true);
        setTimeout(() => {
          this.$parent.openFromCard(this.copiedItem, { skipHistory: true });
          this.$parent.resetCopiedItem();
          this.$parent.setPosition(this.getNextAvailablePosition());
          this.$parent.toggleCart();
          this.$parent.setLoading(false);
          this.$parent.selectPositionContentFromCart();
          this.$parent.setCopyMode();
        }, 2000);
      },
      async copyFreeTextPosition(copyItem) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const sourceItem = this.cartItems.find(item => item.id === copyItem?.id) || copyItem;
        if (!sourceItem?.isFreeText) {
          return;
        }

        const copiedConfig = {
          ...sourceItem,
          id: null,
          items: (sourceItem.items || []).map(item => ({ ...item })),
          sortPosition: this.cartItems.length + 1,
        };
        const artikelJson = this.buildCartItemJson(copiedConfig);

        this.isLoading = true;
        try {
          const response = await api.post('/api/gespeicherteartikel', {
            position_bezeichnung: sourceItem.positionBez || '',
            artikel_json: artikelJson,
            anzahl: 1,
            notizen: sourceItem.notes || '',
            projekt_id: this.selectedProject?.id ?? null,
            ...this.actorParams(),
          });
          const copiedItemId = response?.data?.id ?? null;

          await this.$parent.refreshSavedArticles();
          this.moveCartItemAfter(copiedItemId, sourceItem.id);
          await this.persistCartOrder();
          this.refreshCartSumPrice();
          await this.scrollToItem(copiedItemId);
        } catch (error) {
          console.error('Fehler beim Kopieren der Freitextposition:', error);
          this.dialogMessage = 'Die Freitextposition konnte nicht kopiert werden.';
          this.showDialog = true;
        } finally {
          this.isLoading = false;
          this.copiedItem = null;
        }
      },
      openItemInMainComponent(itemToOpen) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        this.$parent.openFromCard(itemToOpen, { skipHistory: true });
        this.$parent.setPosition(itemToOpen.pos);
        this.$parent.toggleCart();
      },
      newPosition() {
        if (!this.ensureProjectEditable()) {
          return;
        }

        this.$parent.clearFields();
        this.$parent.setPosition(this.getNextAvailablePosition());
        this.$parent.toggleCart();
      },
      openFreeTextDialog(insertAfterId = null) {
        if (USE_INLINE_FREE_TEXT_ENTRY) {
          this.openInlineFreeTextEditor(insertAfterId);
          return;
        }

        if (!this.ensureProjectEditable()) {
          return;
        }

        this.resetFreeTextDraft(insertAfterId);
        this.showFreeTextDialog = true;
      },
      closeFreeTextDialog() {
        this.showFreeTextDialog = false;
        this.freeTextInsertAfterId = null;
      },
      openInlineFreeTextEditor(insertAfterId = null) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        this.resetFreeTextDraft(insertAfterId);
        this.showInlineFreeTextEditor = true;
      },
      closeInlineFreeTextEditor() {
        this.showInlineFreeTextEditor = false;
        this.freeTextInsertAfterId = null;
      },
      resetFreeTextDraft(insertAfterId = null) {
        this.freeTextPositionName = '';
        this.freeTextContent = '';
        this.freeTextTotal = null;
        this.freeTextInsertAfterId = insertAfterId;
      },
      shouldShowInlineFreeTextEditorAfter(item) {
        if (!this.showInlineFreeTextEditor || this.isProjectReadOnly) {
          return false;
        }

        if (this.freeTextInsertAfterId != null) {
          return item?.id === this.freeTextInsertAfterId;
        }

        return this.cartItems[this.cartItems.length - 1] === item;
      },
      openSummaryAdjustmentDialog() {
        if (USE_INLINE_SUMMARY_ADJUSTMENT_ENTRY) {
          this.openInlineSummaryAdjustmentEditor();
          return;
        }

        if (!this.ensureProjectEditable()) {
          return;
        }

        this.resetSummaryAdjustmentDraft();
        this.showSummaryAdjustmentDialog = true;
      },
      closeSummaryAdjustmentDialog() {
        this.showSummaryAdjustmentDialog = false;
      },
      openInlineSummaryAdjustmentEditor() {
        if (!this.ensureProjectEditable()) {
          return;
        }

        this.resetSummaryAdjustmentDraft();
        this.showInlineSummaryAdjustmentEditor = true;
      },
      closeInlineSummaryAdjustmentEditor() {
        this.showInlineSummaryAdjustmentEditor = false;
      },
      resetSummaryAdjustmentDraft() {
        this.summaryAdjustmentText = '';
        this.summaryAdjustmentPercentValue = null;
        this.summaryAdjustmentAmountValue = null;
      },
      async createSummaryAdjustment() {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const text = this.summaryAdjustmentText.trim();
        const draftAdjustment = {
          percentValue: this.summaryAdjustmentPercentValue,
          amountValue: this.summaryAdjustmentAmountValue,
        };
        const configValue = this.getSummaryAdjustmentConfig(draftAdjustment);

        if (!text) {
          return;
        }

        if (this.isSummaryAdjustmentInvalid(draftAdjustment)) {
          return;
        }

        if (this.wouldSummaryAdjustmentDraftExceedNetto()) {
          return;
        }

        const config = {
          type: 'summary_adjustment',
          isSummaryAdjustment: true,
          text,
          calculationType: configValue.calculationType,
          value: configValue.value,
          percentValue: configValue.percentValue,
          amountValue: configValue.amountValue,
        };

        this.isLoading = true;
        try {
          const response = await api.post('/api/gespeicherteartikel', {
            position_bezeichnung: text,
            artikel_json: JSON.stringify(config),
            anzahl: 1,
            notizen: '',
            projekt_id: this.selectedProject?.id ?? null,
            ...this.actorParams(),
          });

          this.summaryAdjustments.push({
            id: response?.data?.id ?? null,
            text,
            calculationType: configValue.calculationType,
            value: configValue.value,
            percentValue: configValue.percentValue,
            amountValue: configValue.amountValue,
            rawJson: JSON.stringify(config),
          });
          await this.$parent.refreshSavedArticles();
          this.closeSummaryAdjustmentDialog();
          this.closeInlineSummaryAdjustmentEditor();
          this.refreshCartSumPrice();
        } catch (error) {
          console.error('Fehler beim Anlegen des Sonderrabatts:', error);
          this.dialogMessage = 'Der Sonderrabatt konnte nicht angelegt werden.';
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      buildSummaryAdjustmentJson(adjustment) {
        const config = this.getSummaryAdjustmentConfig(adjustment);

        return JSON.stringify({
          type: 'summary_adjustment',
          isSummaryAdjustment: true,
          text: adjustment.text,
          calculationType: config.calculationType,
          value: config.value,
          percentValue: config.percentValue,
          amountValue: config.amountValue,
        });
      },
      async persistSummaryAdjustment(adjustment) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        if (!adjustment?.id) {
          return;
        }

        const text = String(adjustment.text || '').trim();
        const config = this.getSummaryAdjustmentConfig(adjustment);

        if (!text) {
          return;
        }

        if (this.isSummaryAdjustmentInvalid(adjustment)) {
          return;
        }

        const pendingAdjustment = {
          ...adjustment,
          calculationType: config.calculationType,
          value: config.value,
          percentValue: config.percentValue,
          amountValue: config.amountValue,
        };
        const candidateAdjustments = (this.summaryAdjustments || []).map(candidate => (
          candidate === adjustment ? pendingAdjustment : candidate
        ));
        if (this.summaryDiscountsExceedNetto(candidateAdjustments, this.netto)) {
          return;
        }

        adjustment.text = text;
        adjustment.value = config.value;
        adjustment.calculationType = config.calculationType;
        adjustment.percentValue = config.percentValue;
        adjustment.amountValue = config.amountValue;
        const rawJson = this.buildSummaryAdjustmentJson(adjustment);
        adjustment.rawJson = rawJson;

        this.isLoading = true;
        try {
          await api.put(`/api/gespeicherteartikel/${adjustment.id}`, {
            position_bezeichnung: text,
            artikel_json: rawJson,
            anzahl: 1,
            notizen: '',
            projekt_id: this.selectedProject?.id ?? null,
            ...this.actorParams(),
          });
          this.refreshCartSumPrice();
        } catch (error) {
          console.error('Fehler beim Aktualisieren des Sonderrabatts:', error);
          this.dialogMessage = 'Der Sonderrabatt konnte nicht aktualisiert werden.';
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      async removeSummaryAdjustment(id) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        if (!id) {
          return;
        }

        this.isLoading = true;
        try {
          await api.delete(`/api/gespeicherteartikel/${id}`, {
            data: this.actorParams(),
          });
          this.summaryAdjustments = this.summaryAdjustments.filter(adjustment => adjustment.id !== id);
          await this.$parent.refreshSavedArticles();
          this.refreshCartSumPrice();
        } catch (error) {
          console.error('Fehler beim Löschen des Sonderrabatts:', error);
          this.dialogMessage = 'Der Sonderrabatt konnte nicht gelöscht werden.';
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      async createFreeTextPosition() {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const text = this.freeTextContent.trim();
        const total = this.parseOptionalNumber(this.freeTextTotal);

        if (!text) {
          return;
        }

        if (total === null || total < 0) {
          this.dialogMessage = 'Bitte geben Sie einen gültigen LP-Wert ein.';
          this.showDialog = true;
          return;
        }

        const config = {
          type: 'free_text',
          isFreeText: true,
          articleId: null,
          articleSearchText: '',
          priorityFolders: null,
          items: [
            {
              id: 'free-text',
              name: '',
              value: text,
            }
          ],
          price: total,
          originalPrice: 0,
          manualListPrice: total,
          manualDiscount: 0,
          manualTotalPrice: null,
          sortPosition: this.cartItems.length + 1,
        };

        this.isLoading = true;
        try {
          const response = await api.post('/api/gespeicherteartikel', {
            position_bezeichnung: this.freeTextPositionName.trim(),
            artikel_json: JSON.stringify(config),
            anzahl: 1,
            notizen: '',
            projekt_id: this.selectedProject?.id ?? null,
            ...this.actorParams(),
          });

          await this.$parent.refreshSavedArticles();
          this.moveCartItemAfter(response?.data?.id ?? null, this.freeTextInsertAfterId);
          await this.persistCartOrder();
          this.closeFreeTextDialog();
          this.closeInlineFreeTextEditor();
          await this.scrollToItem(response?.data?.id ?? null);
        } catch (error) {
          console.error('Fehler beim Anlegen der Freitextposition:', error);
          this.dialogMessage = 'Die Freitextposition konnte nicht angelegt werden.';
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      getNextAvailablePosition() {
        return this.cartItems.length + 1;
      },
      goToDashboard() {
        this.$parent.toggleCart();
        this.$parent.goBack();
      },
      async setCardItems(savedValues) {
        this.isLoading = true;
        try {
          // Den Kontext bei jedem Laden neu uebernehmen. Die Komponente bleibt beim
          // Wechsel zwischen Dashboard und Konfigurator gemountet und wuerde sonst
          // beim naechsten Unternehmen noch dessen alte Rabatte verwenden.
          this.company = this.$parent?.getCompany ? this.$parent.getCompany() : null;
          this.loggedInUser = this.$parent?.getLoggedInUser
            ? this.$parent.getLoggedInUser()
            : this.loggedInUser;
          this.cartItems = [];
          this.summaryAdjustments = [];
          var config = null;
          var cartItem = null;
          for (let index = 0; index < savedValues.length; ++index) {
            const savedValue = savedValues[index];
            config = JSON.parse(savedValue.artikel_json);
            const isPowderFee = config.type === 'powder_fee';
            const isSummaryAdjustment = config.type === 'summary_adjustment'
              || config.isSummaryAdjustment === true
              || isPowderFee;
            if (isSummaryAdjustment) {
              const normalizedConfig = isPowderFee && this.isEmptyNumberInput(config.amountValue)
                ? {
                    ...config,
                    calculationType: 'amount',
                    value: this.parseOptionalNumber(config.value)
                      ?? this.parseOptionalNumber(config.manualListPrice)
                      ?? this.parseOptionalNumber(config.price)
                      ?? 0,
                    percentValue: null,
                    amountValue: this.parseOptionalNumber(config.value)
                      ?? this.parseOptionalNumber(config.manualListPrice)
                      ?? this.parseOptionalNumber(config.price)
                      ?? 0,
                  }
                : config;
              const summaryConfig = this.getSummaryAdjustmentConfig(normalizedConfig);
              this.summaryAdjustments.push({
                id: savedValue.id,
                text: config.text || savedValue.position_bezeichnung || (isPowderFee ? 'Pulverpauschale' : 'Sonderrabatt'),
                calculationType: summaryConfig.calculationType,
                value: summaryConfig.value,
                percentValue: summaryConfig.percentValue,
                amountValue: summaryConfig.amountValue,
                isPowderFee,
                rawJson: savedValue.artikel_json,
              });
              continue;
            }

            const manualDiscount = this.parseOptionalNumber(config.manualDiscount);
            let manualListPrice = this.parseOptionalNumber(config.manualListPrice);
            let manualTotalPrice = this.parseOptionalNumber(config.manualTotalPrice);
            const configuredPrice = this.parseOptionalNumber(config.price) ?? 0;
            const isFreeText = config.type === 'free_text' || config.isFreeText === true;
            // Alte Freitextpositionen speicherten den Eingabewert als GP. Beim Laden
            // wird dieser einmalig als LP interpretiert und beim nächsten Speichern migriert.
            if (isFreeText && manualListPrice === null && configuredPrice <= 0 && manualTotalPrice !== null) {
              manualListPrice = manualTotalPrice;
              manualTotalPrice = null;
            }
            const originalPrice = manualListPrice !== null
              ? (this.parseOptionalNumber(config.originalPrice) ?? 0)
              : configuredPrice;
            const effectivePrice = manualListPrice ?? configuredPrice;
            const isAlternativePosition = this.isAlternativePosition(config);
            const sortPosition = this.parseOptionalNumber(config.sortPosition) ?? index + 1;
            cartItem = {
              id: savedValue.id,
              articleId: config.articleId,
              articleDiscountPosition: config.articleDiscountPosition ?? null,
              positionBez: savedValue.position_bezeichnung,
              articleSearchText: config.articleSearchText,
              priorityFolders: config.priorityFolders ?? null,
              items: config.items || [],
              price: effectivePrice,
              originalPrice,
              productImage: config.productImage || '',
              amount: savedValue.anzahl,
              notes: savedValue.notizen,
              discount: manualDiscount ?? 0.0,
              manualDiscount,
              manualListPrice,
              manualTotalPrice,
              isFreeText,
              isAlternativePosition,
              sortPosition,
              rawJson: savedValue.artikel_json
            };
            this.cartItems.push(cartItem);
          }
          this.sortCartItemsByPosition();
          this.refreshCartItemPostion();
          await Promise.all([
            this.getProjectDiscount(),
            this.getProjectDeliveryConditions(),
            this.getCartExtraCharge(),
          ]);
          await this.$nextTick();
          await this.reloadCartImagesAndSum();
        } finally {
          this.isLoading = false;
        }
      },
      async sendCartItems() {
        this.showSendInquiryConfirmation = false;
        this.showSendCardItemsConfirmation = false;
        this.showSendOfferConfirmation = false;
        if (!this.ensureProjectSendable()) {
          return;
        }
        if (!this.ensureSummaryDiscountLimit()) {
          return;
        }

        this.isLoading = true;

        try {
          await this.ensureCartImagesReadyForPdf();
          const payload = this.buildCartPdfPayload({ includeAlternativePositions: false });
          await api.post('/api/sendcart', payload);
          this.markProjectOrdered();
          this.dialogMessage = 'Ihre Bestellung wurde erfolgreich übermittelt!\n\nEine Kopie Ihrer Bestellung wurde Ihnen per E-Mail zugesendet.';
          this.dialogGoToDashboard = true;
          this.showDialog = true;
        } catch (error) {
          console.error('Es gab einen Fehler beim senden des Warenkorbs: ', error);
          this.dialogMessage = this.getSendErrorMessage(error, 'Die Bestellung konnte nicht gesendet werden.');
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      setSelectedProject(selectedProject) {
        this.selectedProject = selectedProject;
        if (!this.loggedInUser) {
          this.loggedInUser = this.$parent?.getLoggedInUser ? this.$parent.getLoggedInUser() : null;
        }
      },
      markProjectOrdered() {
        if (!this.selectedProject) {
          return;
        }

        this.selectedProject = {
          ...this.selectedProject,
          has_order: true,
          order_sent_at: this.selectedProject.order_sent_at || new Date().toISOString(),
        };

        if (this.$parent?.setSelectedProject) {
          this.$parent.setSelectedProject(this.selectedProject);
        }
      },
      markProjectOfferSent() {
        if (!this.selectedProject) {
          return;
        }

        this.selectedProject = {
          ...this.selectedProject,
          has_offer: true,
          offer_sent_at: this.selectedProject.offer_sent_at || new Date().toISOString(),
        };

        if (this.$parent?.setSelectedProject) {
          this.$parent.setSelectedProject(this.selectedProject);
        }
      },
      markProjectRequestSent() {
        if (!this.selectedProject) {
          return;
        }

        this.selectedProject = {
          ...this.selectedProject,
          has_request: true,
          request_sent_at: this.selectedProject.request_sent_at || new Date().toISOString(),
        };

        if (this.$parent?.setSelectedProject) {
          this.$parent.setSelectedProject(this.selectedProject);
        }
      },
      getSendErrorMessage(error, fallbackMessage) {
        const message = error?.response?.data?.message || fallbackMessage;
        if (String(message).toLowerCase().includes('bereits bestellt')) {
          this.markProjectOrdered();
        }
        if (String(message).toLowerCase().includes('bereits ein angebot')) {
          this.markProjectOfferSent();
        }
        if (String(message).toLowerCase().includes('bereits eine anfrage')) {
          this.markProjectRequestSent();
        }
        return message;
      },
      async sendInquiry() {
        this.showSendInquiryConfirmation = false;
        this.showSendCardItemsConfirmation = false;
        this.showSendOfferConfirmation = false;
        if (!this.ensureRequestSendable()) {
          return;
        }
        if (!this.ensureSummaryDiscountLimit()) {
          return;
        }

        this.isLoading = true;

        try {
          await this.ensureCartImagesReadyForPdf();
          const payload = this.buildCartPdfPayload();
          await api.post('/api/sendrequest', payload);
          this.markProjectRequestSent();
          this.dialogMessage = 'Ihre Anfrage wurde erfolgreich übermittelt!\n\nEine Kopie Ihrer Anfrage wurde Ihnen per E-Mail zugesendet.';
          this.dialogGoToDashboard = true;
          this.showDialog = true;
        } catch (error) {
          console.error('Es gab einen Fehler beim Senden der Anfrage: ', error);
          this.dialogMessage = this.getSendErrorMessage(error, 'Die Anfrage konnte nicht gesendet werden.');
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      async sendOffer() {
        this.showSendInquiryConfirmation = false;
        this.showSendCardItemsConfirmation = false;
        this.showSendOfferConfirmation = false;
        if (!this.ensureProjectSendable()) {
          return;
        }
        if (!this.ensureSummaryDiscountLimit()) {
          return;
        }

        this.isLoading = true;

        try {
          await this.ensureCartImagesReadyForPdf();
          const payload = this.buildCartPdfPayload();
          await api.post('/api/sendoffer', payload);
          this.markProjectOfferSent();
          this.dialogMessage = 'Ihr Angebot wurde erfolgreich übermittelt!';
          this.dialogGoToDashboard = true;
          this.showDialog = true;
        } catch (error) {
          console.error('Es gab einen Fehler beim Senden des Angebots: ', error);
          this.dialogMessage = this.getSendErrorMessage(error, 'Das Angebot konnte nicht gesendet werden.');
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      async downloadRequestPdf() {
        this.showSendInquiryConfirmation = false;
        this.showSendCardItemsConfirmation = false;
        this.showSendOfferConfirmation = false;
        if (!this.ensureSummaryDiscountLimit()) {
          return;
        }
        this.isLoading = true;

        try {
          await this.ensureCartImagesReadyForPdf();
          const payload = this.buildCartPdfPayload();
          const response = await api.post('/api/downloadrequestpdf', payload, {
            responseType: 'blob',
            timeout: 60000,
          });

          const contentDisposition = response.headers?.['content-disposition'];
          const filename = this.extractFilenameFromDisposition(contentDisposition) || this.buildRequestPdfFilename();

          this.downloadBlob(response.data, filename);
        } catch (error) {
          console.error('Es gab einen Fehler beim Erstellen des Anfrage-PDFs: ', error);
          this.dialogMessage = 'Das Anfrage-PDF konnte nicht erstellt werden.';
          this.showDialog = true;
        } finally {
          this.isLoading = false;
        }
      },
      async reloadCartImagesAndSum() {
        await this.$nextTick();
        const cartItemComponents = Array.isArray(this.$refs.cartItemsRefs) ? this.$refs.cartItemsRefs : [];
        await Promise.all(cartItemComponents.map(async component => {
          if (!component || typeof component.loadImageForCart !== 'function') {
            return;
          }

          const imagePath = await component.loadImageForCart();
          if (imagePath) {
            this.updateProductImage({
              id: component.id ?? component.$props?.id,
              newPath: imagePath
            });
          }
        }));
        this.refreshCartSumPrice();
      },
      async ensureCartImagesReadyForPdf() {
        await this.reloadCartImagesAndSum();
      },
      async handleQuantityChanged({ id, amount }) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const item = this.cartItems.find(c => c.id === id);
        if (item) {
          item.amount = amount;
        }
        this.refreshCartSumPrice();
        try {
          await this.persistCartItem(item);
        } catch (e) {
          console.error('Fehler beim Aktualisieren der Menge:', e);
        }
      },
      async handleListPriceChanged({ id, listPrice }) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const item = this.cartItems.find(c => c.id === id);
        if (!item) {
          return;
        }

        const parsedListPrice = this.parseOptionalNumber(listPrice);
        const normalizedListPrice = parsedListPrice !== null && parsedListPrice > 0
          ? parsedListPrice
          : null;
        item.manualListPrice = normalizedListPrice;
        item.price = normalizedListPrice ?? this.parseOptionalNumber(item.originalPrice) ?? 0;
        if (normalizedListPrice !== null) {
          item.manualTotalPrice = null;
        }
        this.refreshCartSumPrice();

        try {
          await this.persistCartItem(item);
        } catch (e) {
          console.error('Fehler beim Aktualisieren des Listenpreises:', e);
        }
      },
      async handleDiscountChanged({ id, discount }) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const item = this.cartItems.find(c => c.id === id);
        if (!item) {
          return;
        }

        const parsedDiscount = Math.min(Math.max(Number(discount) || 0, 0), 100);
        item.discount = parsedDiscount;
        item.manualDiscount = parsedDiscount;
        this.refreshCartSumPrice();

        try {
          await this.persistCartItem(item);
        } catch (e) {
          console.error('Fehler beim Aktualisieren des Rabatts:', e);
        }
      },
      async handleManualTotalChanged({ id, manualTotalPrice }) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const item = this.cartItems.find(c => c.id === id);
        if (!item) {
          return;
        }

        item.manualTotalPrice = this.parseOptionalNumber(manualTotalPrice);
        this.refreshCartSumPrice();

        try {
          await this.persistCartItem(item);
        } catch (e) {
          console.error('Fehler beim Aktualisieren des GP-Werts:', e);
        }
      },
      async handleAlternativePositionChanged({ id, isAlternativePosition }) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const item = this.cartItems.find(c => c.id === id);
        if (!item) {
          return;
        }

        item.isAlternativePosition = !!isAlternativePosition;
        this.refreshCartSumPrice();

        try {
          await this.persistCartItem(item);
        } catch (e) {
          console.error('Fehler beim Aktualisieren der Alternativposition:', e);
        }
      },
      async handleFreeTextDetailsChanged({ id, positionBez, content }) {
        if (!this.ensureProjectEditable()) {
          return;
        }

        const item = this.cartItems.find(c => c.id === id);
        if (!item?.isFreeText) {
          return;
        }

        const text = String(content || '').trim();

        const nextItems = Array.isArray(item.items)
          ? item.items.map(entry => ({ ...entry }))
          : [];
        const preferredIndex = nextItems.findIndex(entry => entry?.id === 'free-text');
        const targetIndex = preferredIndex >= 0 ? preferredIndex : (nextItems.length ? 0 : -1);
        const updatedEntry = {
          ...(targetIndex >= 0 ? nextItems[targetIndex] : {}),
          id: targetIndex >= 0 ? (nextItems[targetIndex]?.id ?? 'free-text') : 'free-text',
          name: targetIndex >= 0 ? (nextItems[targetIndex]?.name ?? '') : '',
          value: text,
        };

        if (targetIndex >= 0) {
          nextItems.splice(targetIndex, 1, updatedEntry);
        } else {
          nextItems.push(updatedEntry);
        }

        item.positionBez = String(positionBez || '').trim();
        item.items = nextItems;

        try {
          await this.persistCartItem(item);
        } catch (error) {
          console.error('Fehler beim Aktualisieren der Freitextposition:', error);
          this.dialogMessage = 'Die Freitextposition konnte nicht aktualisiert werden.';
          this.showDialog = true;
        }
      },
      async refreshCart() {
        this.isLoading = true;
        try {
          this.company = this.$parent.getCompany();
          this.loggedInUser = this.$parent.getLoggedInUser();
          await this.reloadCartImagesAndSum();
        } finally {
          this.isLoading = false;
        }
      },
      refreshCartSumPrice() {
        const toCents = (val) => Math.round((Number(val) || 0) * 100);

        let nettoCents = 0;
        const itemsById = new Map((this.cartItems || []).map(ci => [ci.id, ci]));
        const itemsByPos = new Map((this.cartItems || []).map(ci => [ci.pos, ci]));

        (this.$refs.cartItemsRefs || []).forEach(cartItemComponent => {
          const target =
            itemsById.get(cartItemComponent.id) ||
            itemsById.get(cartItemComponent.$props?.id) ||
            itemsByPos.get(cartItemComponent.pos) ||
            itemsByPos.get(cartItemComponent.$props?.pos);
          const hasComponentAlternativeGetter = typeof cartItemComponent.getAlternativePosition === 'function';
          const isAlternative = hasComponentAlternativeGetter
            ? cartItemComponent.getAlternativePosition()
            : this.isAlternativePosition(target || cartItemComponent);

          // Berücksichtige den Rabatt bei der Berechnung des Nettowerts
          const qty = typeof cartItemComponent.getSelectedQuantity === 'function'
            ? cartItemComponent.getSelectedQuantity()
            : 0;
          let discount = Number(cartItemComponent.discount) || 0;
          if (typeof cartItemComponent.getEffectiveDiscount === 'function') {
            discount = cartItemComponent.getEffectiveDiscount();
          }
          if (typeof cartItemComponent.getDiscountSyncValue === 'function') {
            discount = cartItemComponent.getDiscountSyncValue();
          }
          const lineTotal = typeof cartItemComponent.getLineTotalValue === 'function'
            ? cartItemComponent.getLineTotalValue()
            : this.getCartItemLineTotal(cartItemComponent);
          if (!isAlternative && lineTotal !== null && lineTotal !== undefined) {
            nettoCents += toCents(lineTotal);
          }

          if (target) {
            target.isAlternativePosition = isAlternative;
            if (qty !== null && qty !== undefined && qty !== '') {
              target.amount = qty;
            }

            if (typeof cartItemComponent.getManualListPrice === 'function') {
              const manualListPrice = cartItemComponent.getManualListPrice();
              target.manualListPrice = manualListPrice;
              target.price = manualListPrice ?? this.parseOptionalNumber(target.originalPrice) ?? target.price;
              if (manualListPrice !== null) {
                target.manualTotalPrice = null;
              }
            }

            if (discount !== null && discount !== undefined && discount !== '') {
              target.discount = discount;
            }

            target.manualTotalPrice = typeof cartItemComponent.getManualTotalPrice === 'function'
              ? cartItemComponent.getManualTotalPrice()
              : target.manualTotalPrice;
          }
        });

        this.netto = nettoCents / 100;

        const summaryAdjustmentCents = this.getLimitedSummaryAdjustmentTotalCents(this.summaryAdjustments, this.netto);
        this.summaryAdjustmentTotal = summaryAdjustmentCents / 100;

        const adjustedNettoCents = nettoCents + summaryAdjustmentCents;

        const tzCents = Math.round(adjustedNettoCents * (Number(this.tz) || 0) / 100);
        this.tzNetto = tzCents / 100;
        this.calculatePositionTeuerungszuschlag();

        let lieferKostenCents = 0;
        if (this.lieferBedingungen != null && this.lieferBedingungen.berechnung_vorhanden) {
          const prozent = Number(this.lieferBedingungen.prozent_satz) || 0;
          lieferKostenCents = Math.round(adjustedNettoCents * prozent / 100);

          const minCents = toCents(this.lieferBedingungen.mindest_satz);
          const maxCents = toCents(this.lieferBedingungen.maximal_satz);
          if (lieferKostenCents < minCents) lieferKostenCents = minCents;
          if (maxCents > 0 && lieferKostenCents > maxCents) lieferKostenCents = maxCents;

          const freiAbCents = toCents(this.lieferBedingungen.frei_ab);
          if (freiAbCents > 0 && adjustedNettoCents > freiAbCents) {
            lieferKostenCents = 0;
            this.lieferBedingungen.liefer_text = "Lieferung frei";
          }

          this.lieferBedingungen.liefer_kosten = lieferKostenCents / 100;
        }

        const zwischenCents = adjustedNettoCents + tzCents + lieferKostenCents;
        this.zwischenSum = zwischenCents / 100;

        const mwstCents = Math.round(zwischenCents * 19 / 100);
        this.mwst = mwstCents / 100;

        this.brutto = (zwischenCents + mwstCents) / 100;
      },
      calculatePositionTeuerungszuschlag() {
        const tzRate = Number(this.tz) || 0;
        (this.cartItems || []).forEach(item => {
          item.teuerungszuschlag = null;
          if (this.isAlternativePosition(item) || this.isCartItemPriceOnRequest(item)) {
            return;
          }

          const lineTotalCents = Math.max(
            Math.round((this.getCartItemLineTotal(item) || 0) * 100),
            0
          );
          item.teuerungszuschlag = Math.round(lineTotalCents * tzRate / 100) / 100;
        });
      },
      closeDialog() {
        const shouldGoToDashboard = this.dialogGoToDashboard;
        this.showDialog = false;
        this.dialogGoToDashboard = false;
        if (shouldGoToDashboard) {
          this.goToDashboard();
        }
      },
    },
    beforeUnmount() {
      if (this.orderSaveMessageTimer) {
        clearTimeout(this.orderSaveMessageTimer);
      }
    }
  };
  </script>

<style scoped src="@/styles/WarenkorbComponent.css">

</style>
